import { useState, useRef } from 'react';
import { Camera, ImagePlus, X, Loader2 } from 'lucide-react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { toast } from 'sonner';

export const PhotoCapture = ({ 
  photos = [], 
  onPhotosChange, 
  maxPhotos = 10,
  maxSizeKB = 500,
  disabled = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const compressImage = async (file, maxSizeKB) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if too large
          const maxDimension = 1200;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with high quality and reduce until size is acceptable
          let quality = 0.8;
          let result = canvas.toDataURL('image/jpeg', quality);
          
          while (result.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            result = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(result);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Máximo ${maxPhotos} fotos permitidas`);
      return;
    }
    
    setUploading(true);
    
    try {
      const newPhotos = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error('Solo se permiten imágenes');
          continue;
        }
        
        const compressed = await compressImage(file, maxSizeKB);
        newPhotos.push(compressed);
      }
      
      if (newPhotos.length > 0) {
        onPhotosChange([...photos, ...newPhotos]);
        toast.success(`${newPhotos.length} foto(s) añadida(s)`);
      }
    } catch (error) {
      console.error('Error processing photos:', error);
      toast.error('Error al procesar las fotos');
    } finally {
      setUploading(false);
      // Reset inputs
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  };

  return (
    <div className="space-y-3">
      {/* Photo preview grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
              <img 
                src={photo} 
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Hidden file inputs */}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleFileSelect}
        ref={cameraInputRef}
        className="hidden"
      />
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        ref={galleryInputRef}
        className="hidden"
      />
      
      {/* Photo capture button with dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full"
            disabled={disabled || uploading || photos.length >= maxPhotos}
            data-testid="add-photo-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                Añadir foto ({photos.length}/{maxPhotos})
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48">
          <DropdownMenuItem onClick={() => cameraInputRef.current?.click()}>
            <Camera className="w-4 h-4 mr-2" />
            Hacer foto
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => galleryInputRef.current?.click()}>
            <ImagePlus className="w-4 h-4 mr-2" />
            Elegir de galería
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default PhotoCapture;
